/**
 * Landing Page Sample Data
 *
 * This file contains curated sample data for the landing page demonstrations.
 * It includes:
 * - Sample performance data for analytics showcases
 * - Statistics for social proof
 * - Demo questions for interactive widgets (to be added in Phase 2)
 */

import { Question } from '../types';

/**
 * Sample Student Performance Data
 * Used for the hero section RadarChart and analytics showcases
 */
export const samplePerformanceData = {
  labels: [
    'Number & Algebra',
    'Measurement',
    'Geometry',
    'Statistics',
    'Probability'
  ],
  data: [85, 72, 78, 90, 68],
  title: 'Performance by Strand'
};

/**
 * Sample Statistics for Hero Section
 * Real data from database
 */
export const heroStatistics = {
  totalQuestions: '5,700+',
  syllabusPoints: '168',
  stages: 'Stages 4-5'
};

/**
 * Sample Stats for Statistics Bar (Phase 5)
 * These provide social proof and showcase platform capabilities
 */
export const platformStatistics = [
  {
    icon: 'questions',
    number: '5,700+',
    label: 'Practice Questions',
    description: 'Comprehensive question bank covering NSW syllabus points'
  },
  {
    icon: 'syllabus',
    number: '168',
    label: 'Syllabus Points',
    description: 'Complete alignment with NSW Mathematics curriculum'
  },
  {
    icon: 'solutions',
    number: '100%',
    label: 'Worked Solutions',
    description: 'Every question includes detailed explanations and step-by-step solutions'
  },
  {
    icon: 'analytics',
    number: 'Real-time',
    label: 'Performance Analytics',
    description: 'Track progress across strands, substrands, and individual topics'
  }
];

/**
 * Enhanced Sample Performance Data with Additional Metrics
 * For use in Phase 3 Analytics Showcase
 */
export const enhancedPerformanceData = {
  radarChart: samplePerformanceData,
  statsCards: [
    {
      label: 'Quizzes Completed',
      value: 47,
      trend: '+12',
      trendDirection: 'up' as const,
      description: 'from last month'
    },
    {
      label: 'Average Score',
      value: '82%',
      trend: '+15%',
      trendDirection: 'up' as const,
      description: 'from last month'
    },
    {
      label: 'Current Streak',
      value: '12 days',
      trend: 'Personal best!',
      trendDirection: 'neutral' as const,
      description: 'Keep it going!'
    },
    {
      label: 'Topics Mastered',
      value: 34,
      trend: '+8',
      trendDirection: 'up' as const,
      description: 'this month'
    }
  ],
  overallPercentage: 78,
  totalQuizAttempts: 47,
  currentStreak: 12
};

/**
 * Trust Indicators for Hero Section
 */
export const trustIndicators = [
  {
    icon: 'check',
    text: 'Free 30-day trial'
  },
  {
    icon: 'check',
    text: 'No credit card required'
  },
  {
    icon: 'check',
    text: 'Cancel anytime'
  }
];

/**
 * Value Propositions for Subheadline
 */
export const valuePropositions = {
  primary: 'Help Your Child Master the NSW Maths Curriculum with Confidence',
  secondary: '5,700+ practice questions • Fully aligned with NSW syllabus • Instant feedback & worked solutions',
  heroDescription: 'Track progress in real-time, identify knowledge gaps early, and watch your child excel in mathematics with our comprehensive, curriculum-aligned platform.'
};

/**
 * Transparency Data for Trust Building Section
 * REAL DATA from ThinkSphere database - shows actual syllabus code mappings with question counts
 */
export const transparencyData = {
  credibilityPoints: [
    'Every question reviewed by NSW curriculum experts',
    'Questions mapped to 168 specific syllabus content points',
    'Complete alignment with NSW Mathematics syllabus',
    'Detailed worked solutions for every single question'
  ],
  syllabusExamples: [
    {
      year: 'Years 7-8',
      topic: 'Fractions & Decimals',
      syllabusCode: 'MA4-5NA',
      questionCount: 410,
      description: 'Operates with fractions, decimals and percentages'
    },
    {
      year: 'Years 7-8',
      topic: 'Pythagoras Theorem',
      syllabusCode: 'MA4-16MG',
      questionCount: 380,
      description: 'Right-angled triangles and Pythagoras theorem'
    },
    {
      year: 'Years 7-8',
      topic: 'Ratios & Rates',
      syllabusCode: 'MA4-7NA',
      questionCount: 360,
      description: 'Operates with ratios and rates'
    },
    {
      year: 'Years 7-8',
      topic: 'Geometry',
      syllabusCode: 'MA4-17MG',
      questionCount: 308,
      description: 'Properties of triangles and quadrilaterals'
    },
    {
      year: 'Years 7-8',
      topic: 'Linear Relationships',
      syllabusCode: 'MA4-11NA',
      questionCount: 280,
      description: 'Graphs and analyses linear relationships'
    },
    {
      year: 'Years 7-8',
      topic: 'Data Analysis',
      syllabusCode: 'MA4-19SP',
      questionCount: 243,
      description: 'Data collection and representation'
    },
    {
      year: 'Years 7-8',
      topic: 'Indices',
      syllabusCode: 'MA4-9NA',
      questionCount: 240,
      description: 'Operates with positive-integer indices'
    },
    {
      year: 'Years 7-8',
      topic: 'Area',
      syllabusCode: 'MA4-13MG',
      questionCount: 222,
      description: 'Areas of quadrilaterals and circles'
    }
  ]
};

/**
 * Competitive Comparison Data
 * Shows ThinkSphere advantages vs. other platforms for credibility building
 */
export const competitiveComparisonData = {
  platforms: [
    {
      name: 'ThinkSphere',
      features: {
        curriculumSpecific: '✅ 168 content points',
        workedSolutions: '✅ Every question',
        progressTracking: '✅ Detailed radar charts',
        questionCount: '✅ 5,700+',
        cost: '$4.99/week'
      }
    },
    {
      name: 'Khan Academy',
      features: {
        curriculumSpecific: '❌ Global only',
        workedSolutions: '⚠️ Video only',
        progressTracking: '❌ Basic scores',
        questionCount: '❌ Not specified',
        cost: 'Free (ads)'
      }
    },
    {
      name: 'Mathletics',
      features: {
        curriculumSpecific: '⚠️ Generic Australian',
        workedSolutions: '⚠️ Selected questions',
        progressTracking: '⚠️ Limited analytics',
        questionCount: '❌ Not specified',
        cost: '$15/month'
      }
    }
  ],
  featureLabels: {
    curriculumSpecific: 'NSW-specific curriculum',
    workedSolutions: 'Worked solutions',
    progressTracking: 'Progress by substrand',
    questionCount: 'Question count (NSW)',
    cost: 'Cost'
  }
};

/**
 * Demo Questions for Interactive Widget (Phase 2)
 * Curated sample questions showcasing platform features
 */
export const demoQuestions: Question[] = [
  {
    questionId: 1,
    questionDescription: 'What is 15 × 12?',
    questionType: 'multiple-choice',
    difficulty: 'easy',
    optionsJson: {
      A: '150',
      B: '180',
      C: '200',
      D: '165'
    },
    answerKey: 'B',
    explanation: 'To multiply 15 by 12, we can break it down into easier steps. First, multiply 15 × 10 = 150. Then, multiply 15 × 2 = 30. Finally, add them together: 150 + 30 = 180.',
    workedSolution: `**Step-by-step Solution:**

1. Break down 12 into 10 + 2
2. Calculate 15 × 10 = 150
3. Calculate 15 × 2 = 30
4. Add the results: 150 + 30 = 180

**Alternative method (traditional multiplication):**
- 15 × 12
- = 15 × (10 + 2)
- = (15 × 10) + (15 × 2)
- = 150 + 30
- = 180

**Answer: B. 180**`
  },
  {
    questionId: 2,
    questionDescription: 'Solve for x: 3x + 7 = 22',
    questionType: 'multiple-choice',
    difficulty: 'medium',
    optionsJson: {
      A: 'x = 3',
      B: 'x = 5',
      C: 'x = 7',
      D: 'x = 15'
    },
    answerKey: 'B',
    explanation: 'To solve for x, we need to isolate it on one side of the equation. First, subtract 7 from both sides to get 3x = 15. Then, divide both sides by 3 to find x = 5.',
    workedSolution: `**Step-by-step Solution:**

Given equation: 3x + 7 = 22

1. **Subtract 7 from both sides:**
   - 3x + 7 - 7 = 22 - 7
   - 3x = 15

2. **Divide both sides by 3:**
   - 3x ÷ 3 = 15 ÷ 3
   - x = 5

3. **Check the answer** (substitute x = 5 back into original equation):
   - 3(5) + 7 = 15 + 7 = 22 ✓

**Answer: B. x = 5**`
  },
  {
    questionId: 3,
    questionDescription: 'A rectangle has a length of 8 cm and a width of 5 cm. What is its area?',
    questionType: 'multiple-choice',
    difficulty: 'easy',
    optionsJson: {
      A: '13 cm²',
      B: '26 cm²',
      C: '40 cm²',
      D: '80 cm²'
    },
    answerKey: 'C',
    explanation: 'The area of a rectangle is calculated using the formula: Area = length × width. With a length of 8 cm and width of 5 cm, we multiply: 8 × 5 = 40 cm².',
    workedSolution: `**Step-by-step Solution:**

**Formula:** Area of rectangle = length × width

Given:
- Length = 8 cm
- Width = 5 cm

**Calculate the area:**
- Area = 8 × 5 = 40 cm²

**Visual representation:**
\`\`\`
┌─────────────────┐
│                 │  ← 5 cm
│                 │
└─────────────────┘
      8 cm
\`\`\`

**Answer: C. 40 cm²**`
  }
];
